import React, { useState, useEffect } from 'react';
import Spline from '@splinetool/react-spline';
import { motion } from 'framer-motion';
import { FaEnvelope, FaGithub, FaLinkedin } from 'react-icons/fa';
import './App.css';

export default function App() {
  const [splineLoaded, setSplineLoaded] = useState(false);
  const [showContent, setShowContent] = useState(false);

  // Forcefully hide the Spline logo using DOM manipulation
  useEffect(() => {
    const hideSplineLogo = () => {
      // Find all anchor tags
      const links = document.querySelectorAll('a');
      links.forEach(link => {
        if (link.href && link.href.includes('spline.design')) {
          link.style.display = 'none';
          link.style.opacity = '0';
          link.style.pointerEvents = 'none';
        }
      });

      // Also look for shadow DOMs if Spline is using web components
      const splineViewer = document.querySelector('spline-viewer');
      if (splineViewer && splineViewer.shadowRoot) {
        const shadowLogo = splineViewer.shadowRoot.querySelector('#logo');
        if (shadowLogo) {
          shadowLogo.style.display = 'none';
        }
      }
    };

    hideSplineLogo();
    const intervalId = setInterval(hideSplineLogo, 100); // Check every 100ms
    return () => clearInterval(intervalId);
  }, []);

  // Trigger the movement immediately after the spline model loads
  useEffect(() => {
    if (splineLoaded) {
      const timer = setTimeout(() => {
        setShowContent(true);
      }, 10000); // Wait 10 seconds in the center before moving
      return () => clearTimeout(timer);
    }
  }, [splineLoaded]);

  const experiences = [
    {
      name: "VectorShift Full-Stack SDE Internship",
      description: "Engineered scalable web applications focusing on node abstraction interfaces and directed acyclic graph (DAG) logic optimization. Developed responsive, low-latency frontends utilizing advanced CSS techniques while streamlining native backend workflows to enhance system performance and multi-agent interaction.",
      tech_stack: ["React.js", "Next.js", "Node.js", "Express", "Python", "FastAPI"]
    },
    {
      name: "GirlScript Summer of Code (GSSoC 2024)",
      description: "Contributed to high-impact open-source software by optimizing features, debugging complex codebase architectures, and collaborating within a large-scale development ecosystem to scale community-driven projects.",
      tech_stack: ["Git", "GitHub", "JavaScript", "React.js", "Open Source"]
    }
  ];

  const projects = [
    {
      name: "HikariStoryboards",
      description: "An AI-powered storyboard generator that streamlines the creative process with interactive canvases.",
      tech_stack: ["React", "Flask", "AI"],
      link: "https://hikaristoryboard.netlify.app/",
      image: "/Projects/HikariStoryBoard.png"
    },
    {
      name: "Shelf Search",
      description: "A comprehensive digital library management system featuring advanced search and categorization.",
      tech_stack: ["React", "JavaScript"],
      link: "https://shelfsearch.netlify.app/",
      image: "/Projects/ShelfSearch.png"
    },
    {
      name: "Luminara",
      description: "A fully immersive WebGL experience rendering beautiful lighting and 3D architectural scenes.",
      tech_stack: ["React.js", "Express.js", "NASA API"],
      link: "https://luminarcelestial.netlify.app/",
      image: "/Projects/Luminara.png"
    },
    {
      name: "ScamSlam",
      description: "A machine-learning powered application built to detect and flag fraudulent schemes and phishing emails.",
      tech_stack: ["Python", "Django", "ML"],
      link: "https://github.com/Ananya-vastare/ScamSlam",
      image: "/Projects/ScamSlam.png"
    },
    {
      name: "Shikshana",
      description: "An accessible educational platform for providing remote learning resources to underserved communities.",
      tech_stack: ["React Native", "Firebase"],
      link: "https://shikshanaroadmap.netlify.app/",
      image: "/Projects/Shikshana.png"
    },
    {
      name: "Anvestra",
      description: "An AI-powered platform for UI/UX design evaluation.",
      tech_stack: ["React.js", "Flask API"],
      link: "https://anvestra.netlify.app/",
      image: "/Projects/Anvestra.png"
    }, {
      name: "AI-Aware Rate Limiter",
      description: "A production-style backend system that enforces Requests Per Minute (RPM) and Tokens Per Minute (TPM) limits for AI/LLM APIs using thread-safe in-memory storage and RESTful APIs.",
      tech_stack: ["Java 21", "Spring Boot 3", "Docker", "JUnit"],
      link: "https://github.com/Ananya-vastare/AI-Rate-limiter",

    },
    {
      name: "Redis Analyzer",
      description: "A Redis-inspired in-memory key-value database built from scratch using TCP sockets, featuring persistence, TTL support, and an AI-powered database health analyzer.",
      tech_stack: ["Node.js", "JavaScript", "TCP Sockets", "JSON"],
      link: "https://github.com/Ananya-vastare/Redis-with-analyzer-",
    }
  ];

  const skills = [
    { name: "React.js", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg" },
    { name: "JavaScript", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/javascript/javascript-original.svg" },
    { name: "TypeScript", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/typescript/typescript-original.svg" },
    { name: "C", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/c/c-original.svg" },
    { name: "C++", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/cplusplus/cplusplus-original.svg" },
    { name: "Python", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg" },
    { name: "AI / ML", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tensorflow/tensorflow-original.svg" },
    { name: "Generative AI", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/openai/openai-original.svg" },
    { name: "MongoDB", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mongodb/mongodb-original.svg" },
    { name: "SQL", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original.svg" },
    { name: "Express.js", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/express/express-original.svg" }
  ];

  return (
    <div className="app-container">

      {/* Hero Section (100vh) */}
      <section className="hero-section" style={{ height: '100vh', position: 'relative', overflow: 'hidden', width: '100%' }}>

        {/* Subtle Background Accents */}
        <div className="bg-accents">
          <div className="bg-shape shape-1"></div>
          <div className="bg-shape shape-2"></div>
          <div className="bg-shape shape-3"></div>
          <div className="constellation star-1"></div>
          <div className="constellation star-2"></div>
          <div className="constellation star-3"></div>
          <div className="constellation star-4"></div>
          <div className="constellation star-5"></div>
        </div>

        {/* The Spline Model Container - Absolute to Hero */}
        <motion.div
          className="spline-wrapper"
          initial={{ width: '100%', right: '0px' }}
          animate={{
            width: showContent ? '50%' : '100%',
            right: showContent ? '-180px' : '0px'
          }}
          transition={{ duration: 1.5, ease: [0.76, 0, 0.24, 1] }}
        >
          <Spline
            scene="https://prod.spline.design/31jrOGj-rGSP0gms/scene.splinecode"
            onLoad={() => setSplineLoaded(true)}
          />
        </motion.div>

        {/* The Text Content Container */}
        <motion.div
          className="content-wrapper"
          initial={{ opacity: 0, x: -50 }}
          animate={{
            opacity: showContent ? 1 : 0,
            x: showContent ? 0 : -50
          }}
          transition={{ duration: 1.2, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          style={{ pointerEvents: showContent ? 'auto' : 'none' }}
        >
          <h1>ANANYA Ravikiran <br />
            VASTARE</h1>
          <p>Building Products That Scale</p>
          <button className="portfolio-btn" onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}>Explore My Work</button>
        </motion.div>
      </section>

      {/* Experience Section */}
      {showContent && (
        <section className="experience-section">
          <motion.h2
            className="experience-title"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            My Experience
          </motion.h2>

          <div className="cards-container">
            {experiences.map((exp, index) => (
              <motion.div
                className="modern-card"
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <h3>{exp.name}</h3>
                <p>{exp.description}</p>
                <div className="tech-tags">
                  {exp.tech_stack.map((tech, i) => (
                    <span className="tech-tag" key={i}>{tech}</span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Projects Section */}
      {showContent && (
        <section className="projects-section">
          <motion.h2
            className="experience-title"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            My Projects
          </motion.h2>

          <div className="cards-container">
            {projects.map((proj, index) => (
              <motion.div
                className="modern-card project-card"
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="project-image">
                  <img src={proj.image} alt={proj.name} />
                </div>
                <div className="project-content">
                  <h3>{proj.name}</h3>
                  <p>{proj.description}</p>
                  <div className="tech-tags">
                    {proj.tech_stack.map((tech, i) => (
                      <span className="tech-tag" key={i}>{tech}</span>
                    ))}
                  </div>
                  <a
                    href={proj.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="project-view-btn"
                  >
                    View Project
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Skills Section */}
      {showContent && (
        <section className="skills-section">
          <motion.div
            className="skills-header"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="skills-title">SKILLS</h2>
            <p className="skills-subtitle">The technologies and tools I leverage to build scalable applications.</p>
          </motion.div>

          <div className="skills-grid">
            {skills.map((skill, index) => (
              <motion.div
                className="skill-card"
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.05,
                  type: "spring",
                  stiffness: 100
                }}
                whileHover={{
                  scale: 1.15,
                  rotate: index % 2 === 0 ? 4 : -4,
                  y: -15,
                  zIndex: 10,
                  transition: { type: "spring", stiffness: 300, damping: 10 }
                }}
                drag
                dragConstraints={{ left: -15, right: 15, top: -15, bottom: 15 }}
                dragElastic={0.2}
                whileTap={{ scale: 0.95, cursor: "grabbing" }}
                style={{ cursor: "grab" }}
              >
                <div className="skill-icon-wrapper">
                  <img src={skill.icon} alt={skill.name} className="skill-icon" />
                </div>
                <span className="skill-name">{skill.name}</span>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Contact Section */}
      {showContent && (
        <section className="contact-section">
          <div className="contact-container">
            {/* Left Side: All Contact Content */}
            <div className="contact-left-content">
              <motion.h2
                className="experience-title"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                Let's Connect
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="contact-description"
              >
                Feel free to reach out for collaborations, opportunities, or just a quick hello. I'm always open to discussing new projects and creative ideas.
              </motion.p>

              <div className="contact-links-grid">
                <motion.a
                  href="mailto:ananyarvastare@gmail.com"
                  className="contact-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                >
                  <div className="icon"><FaEnvelope /></div>
                  <h3>Email Me</h3>
                  <p>ananyarvastare@gmail.com</p>
                </motion.a>

                <motion.a
                  href="https://github.com/Ananya-vastare"
                  target="_blank" rel="noopener noreferrer"
                  className="contact-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <div className="icon"><FaGithub /></div>
                  <h3>GitHub</h3>
                  <p>@Ananya-vastare</p>
                </motion.a>

                <motion.a
                  href="https://www.linkedin.com/in/ananya-ravikiran-vastare-17037b250/"
                  target="_blank" rel="noopener noreferrer"
                  className="contact-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <div className="icon"><FaLinkedin /></div>
                  <h3>LinkedIn</h3>
                  <p>Connect with me</p>
                </motion.a>
              </div>
            </div>
          </div>
        </section>
      )}

    </div>
  );
}
